import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
    Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { BookmarkPlus, ChevronDown, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function PresetBar({ filters, sort, onApply }) {
    const [presets, setPresets] = useState([]);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");

    const reload = async () => {
        try {
            const res = await api.get("/presets");
            setPresets(res.data.items || []);
        } catch (_) { /* ignored when not authed */ }
    };
    useEffect(() => { reload(); }, []);

    const save = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) return;
        try {
            const res = await api.post("/presets", { name: name.trim(), filters, sort });
            setPresets((ps) => [res.data, ...ps]);
            setName("");
            setOpen(false);
            toast.success(`Saved preset "${res.data.name}"`);
        } catch { toast.error("Could not save preset"); }
    };

    const apply = (p) => {
        onApply({ filters: p.filters, sort: p.sort });
        toast.success(`Applied "${p.name}"`);
    };

    return (
        <div className="flex items-center gap-2" data-testid="preset-bar">
            {presets.length > 0 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="presets-trigger">
                            <BookmarkPlus className="h-3.5 w-3.5" />
                            Presets
                            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[220px]">
                        <DropdownMenuLabel>My presets</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {presets.map((p) => (
                            <DropdownMenuItem
                                key={p.preset_id}
                                onClick={() => apply(p)}
                                className="cursor-pointer"
                                data-testid={`apply-preset-${p.preset_id}`}
                            >
                                {p.name}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="save-preset-trigger">
                        <Save className="h-3.5 w-3.5" />
                        Save preset
                    </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Save current filters</DialogTitle>
                        <DialogDescription>
                            Save this combination of filters and sort to apply with one click later.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save}>
                        <Input
                            autoFocus
                            placeholder="e.g. High-yield kings, payout < 70%"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={80}
                            data-testid="preset-name-input"
                        />
                        <DialogFooter className="mt-4">
                            <DialogClose asChild>
                                <Button type="button" variant="ghost"><X className="h-4 w-4" />Cancel</Button>
                            </DialogClose>
                            <Button type="submit" disabled={!name.trim()} data-testid="preset-save-btn">
                                <Save className="h-4 w-4" />Save
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
